import { useSelector } from 'react-redux'
import { memo, useCallback } from 'react'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import * as Styled from './styled'
import { COLORS } from 'consts/colors'
import { THUNDER_ICON } from 'consts/mediaUrls'
import Tooltip from 'components/Tooltip'

import { CrownIcon } from 'containers/SubSidebar/styled'
import VideoGenerationCredits from './Credits'
import PowerMode from './PowerMode'
import { useGenerateAction } from 'hooks/useGenerateVideo'
import { isSmartEditModeSelector } from 'store/selectors'
import { useRunWithPowerModeRetry } from 'context/PowerModeRetryContext'

interface IFooterProps {
  isGenerateDisabled?: boolean
}

const Footer = (props: IFooterProps) => {
  const { isGenerateDisabled } = props

  const isGenerateButtonActive = useSelector(
    aiVideoSelectors.isGenerateButtonActive
  )
  const isRequestingGeneration = useSelector(
    aiVideoSelectors.isRequestingGeneration
  )
  const isSmartEditMode = useSelector(isSmartEditModeSelector)
  const smartPrompt = useSelector(aiVideoSelectors.smartPrompt)
  const isSmartEditDisabled = isSmartEditMode && !smartPrompt

  const {
    handleGenerate,
    generateButtonLabel,
    tooltipText,
    hasInsufficientCredits,
    generationCredits,
    isApproximateCost,
    imageResizeCost
  } = useGenerateAction()

  const runWithRetry = useRunWithPowerModeRetry()
  const onGenerateClick = useCallback(() => {
    runWithRetry(handleGenerate)
  }, [runWithRetry, handleGenerate])

  const isEffectivelyDisabled = isGenerateDisabled || isSmartEditDisabled
  const isButtonDisabled =
    (!isGenerateButtonActive || isEffectivelyDisabled) &&
    !hasInsufficientCredits
  const isTooltipVisible =
    ((!isGenerateButtonActive && !isRequestingGeneration) ||
      isEffectivelyDisabled ||
      imageResizeCost > 0) &&
    !hasInsufficientCredits

  const TooltipComponent =
    imageResizeCost > 0 ? Styled.ReferenceResizeTooltip : Tooltip

  return (
    <Styled.Footer>
      <PowerMode />
      <TooltipComponent
        tooltipText={isTooltipVisible ? tooltipText : ''}
        isVisible={isTooltipVisible}
      >
        <div>
          <Styled.GenerateButton
            isDisabled={isButtonDisabled}
            onClick={onGenerateClick}
            $isPremium={hasInsufficientCredits}
          >
            {hasInsufficientCredits && <CrownIcon />}
            <Styled.SpinningLoaderWrapper
              size={16}
              loaderColor={COLORS.white}
              $isVisible={isRequestingGeneration}
            />
            {generateButtonLabel}
            <Styled.CreditsBadge>
              <Styled.ThunderIcon src={THUNDER_ICON} alt="credits" />
              {isApproximateCost ? `~${generationCredits}` : generationCredits}
            </Styled.CreditsBadge>
          </Styled.GenerateButton>
        </div>
      </TooltipComponent>
      <VideoGenerationCredits />
    </Styled.Footer>
  )
}

export default memo(Footer)
